import { cineamoApiClient } from '../../apiClient';
import { CinemaSubscriptionProgramEnum } from '../../constants/CinemaSubscriptionProgram.enum';
import { RequestConfig } from '../../fetch-helper';
import { revalidateGetCinema } from '../../helpers/revalidation-helper';
import { UrlQueryParameter, buildUrlQuery } from '../../helpers/url-query-helper';
import { CinemaDto } from '../../models/cinema/CinemaDto.types';
import { CinemaInputDto } from '../../models/cinema/CinemaInputDto';
import { ImageDto } from '../../models/image/ImageDto.types';
import { ApiErrorResponse } from '../../types/ApiErrorResponse.types';
import { CinemaImageListResponse, CinemaListResponse } from '../../types/ApiListResponse.types';
import { ApiResponse } from '../../types/ApiResponse.types';

import { CinemaApiEmbed } from './cinema-api.embed';

// <------------------------ Single Cinema ------------------------>

/**
 * This endpoint returns a single cinema.
 *
 * @example
 * // Get the details of a single cinema by cinemaId or slug:
 * getCinemaById(1);
 * getCinemaById(1, { embed: [CinemaApiEmbed.CINEMA_CONFIGURATION] });
 *
 * @param id
 * @param params - Optional query parameters (e.g. embed)
 * @returns {ApiResponse<CinemaDto>}
 */

export async function getCinemaById(id: number | string, params?: UrlQueryParameter): Promise<ApiResponse<CinemaDto>> {
    return cineamoApiClient
        .get<ApiResponse<CinemaDto>>(`/cinemas/${id}${buildUrlQuery(params)}`)
        .then((response) => response.data)
        .catch(() => {
            return null;
        });
}

// <------------------------ Filtered Cinema List ------------------------>

export type GetCinemasQueryParameter = {
    isPrivateBookingPartner?: boolean;
    isActive?: boolean;
    countryCode?: string;
    employedUserId?: string | number;
    favoritedUserId?: string | number;
    cinemaChainId?: string | number;
    cinemaCompanyIds?: number[];
    cinemaChainIds?: number[];
    /** City relation ids — distinct from the denormalised city name string. */
    cityIds?: number[];
    longitude?: number;
    latitude?: number;
    distance?: number;
    embed?: CinemaApiEmbed[];
    metricStartDatetime?: string;
    metricEndDatetime?: string;
    isWebsiteCustomer?: boolean;
    hasMarketingAssistantBooked?: boolean;
    /** ANY-match: cinemas participating in at least one of the given programs. */
    subscriptionPrograms?: CinemaSubscriptionProgramEnum[];
    hasSubscriptionProgram?: boolean;
    /** Numeric Movie id — cinemas that have at least one showing of that movie. */
    movieId?: number;
    /** ISO datetime; narrows the showings considered by `movieId`. */
    showingDatetimeStart?: string;
    /** ISO datetime; narrows the showings considered by `movieId`. */
    showingDatetimeEnd?: string;
} & UrlQueryParameter;

/**
 * This endpoint returns a filtered list of cinemas.
 *
 * @example
 * // Get a list cinemas filtered according to query parameters used:
 * getCinemas({isActive: true});
 *
 * @returns {ApiResponse<unknown, CinemaListResponse>}
 */

export async function getCinemas(
    params?: GetCinemasQueryParameter,
    config?: RequestConfig
): Promise<ApiResponse<CinemaListResponse>> {
    return cineamoApiClient
        .get<ApiResponse<CinemaListResponse>>(`/cinemas${buildUrlQuery(params)}`, config)
        .then((response) => response.data)
        .catch(() => {
            return null;
        });
}

/**
 * Returns the cinemas of one city. The city is part of the path, so the scope cannot be overridden
 * by a query param — prefer this over `getCinemas({ cityIds })` when the city is already known.
 *
 * @example
 * // Get the active cinemas of city 7:
 * getCinemasByCityId(7, { isActive: true });
 *
 * @param cityId
 * @param params - Optional query parameters (everything getCinemas accepts except cityIds)
 * @returns {ApiResponse<CinemaListResponse>}
 */

export async function getCinemasByCityId(
    cityId: number | string,
    params?: Omit<GetCinemasQueryParameter, 'cityIds'>,
    config?: RequestConfig
): Promise<ApiResponse<CinemaListResponse>> {
    return cineamoApiClient
        .get<ApiResponse<CinemaListResponse>>(`/cities/${cityId}/cinemas${buildUrlQuery(params)}`, config)
        .then((response) => response.data)
        .catch(() => {
            return null;
        });
}

// <------------------------ Create Cinema ------------------------>

/**
 * This endpoint creates a single cinema and returns a cinema object or an error.
 *
 * @example
 * // Create a single cinema from form data:
 * createCinema({...cinema});
 *
 * @param cinema
 * @returns {ApiResponse<CinemaDto> | ApiErrorResponse<CinemaDto>}
 */

export async function createCinema(
    cinema: CinemaInputDto
): Promise<ApiResponse<CinemaDto> | ApiErrorResponse<CinemaInputDto>> {
    return cineamoApiClient
        .post('/cinemas', cinema)
        .then((response) => response.data)
        .catch((error) => error);
}

// <------------------------ Update Cinema ------------------------>

/**
 * This endpoint updates a single cinema and returns the updated cinema object or an error.
 *
 * @example
 * // Update the details of a single cinema from form data:
 * updateCinema({isActive: true});
 *
 * @param cinema
 * @returns {ApiResponse<CinemaDto>}
 */

export async function updateCinema(
    cinema: CinemaInputDto
): Promise<ApiResponse<CinemaDto> | ApiErrorResponse<CinemaInputDto>> {
    return cineamoApiClient
        .patch(`/cinemas/${cinema.id}`, cinema)
        .then((res) => {
            revalidateGetCinema({
                cinemaId: cinema.id
            });
            return res.data;
        })
        .catch((error) => error);
}

export async function cinemaShowtimesSync(cinemaId: string | number): Promise<ApiResponse | ApiErrorResponse> {
    return cineamoApiClient
        .post(`/cinemas/${cinemaId}/showtimes-sync`)
        .then((res) => res.status === 204)
        .catch((error) => error);
}

export type CinemaShowtimesCrawlResponse = {
    sourceCount: number;
    errors: string[];
    message: string;
};

export async function cinemaShowtimesCrawl(
    cinemaId: string | number
): Promise<CinemaShowtimesCrawlResponse | ApiErrorResponse> {
    return cineamoApiClient
        .post(`/cinemas/${cinemaId}/showtimes-crawl`)
        .then((res) => res.data)
        .catch((error) => error);
}

export type CinemaShowtimesCrawlBatchResponse = {
    message: string;
};

export async function cinemaShowtimesCrawlBatch(): Promise<CinemaShowtimesCrawlBatchResponse | ApiErrorResponse> {
    return cineamoApiClient
        .post('/cinemas/showtimes-crawl-batch')
        .then((res) => res.data)
        .catch((error) => error);
}

// <------------------------ Cinema Images ------------------------>

/**
 * This endpoint returns a list of Image objects (Cinema images).
 *
 * @example
 * // Get a list of cinema images:
 * getCinemaImages(1);
 *
 * @param cinemaId
 * @param urlParams
 * @returns {ApiResponse<unknown, CinemaImageListResponse>}
 */

export async function getCinemaImages(
    cinemaId: CinemaDto['id'],
    urlParams?: UrlQueryParameter
): Promise<ApiResponse<unknown, CinemaImageListResponse>> {
    return cineamoApiClient
        .get<ApiResponse<CinemaImageListResponse>>(`/cinemas/${cinemaId}/images${buildUrlQuery(urlParams)}`)
        .then((response) => {
            return response.data;
        })
        .catch(() => {
            return null;
        });
}

/**
 * This endpoint sets the list of Image objects (Cinema images), in the given order.
 *
 * @example
 * // Set the cinema images (order + alt texts) from a list of images:
 * updateCinemaImages(1, [{ imageRef: 'a3f1...', imageDescription: 'Foyer' }, { imageRef: 'b7e2...' }]);
 *
 * @param cinemaId
 * @param images
 * @returns {ApiResponse<unknown, CinemaImageListResponse> | ApiErrorResponse<unknown>}
 */

export async function updateCinemaImages(
    cinemaId: CinemaDto['id'],
    images: Pick<ImageDto, 'imageRef' | 'imageDescription'>[]
): Promise<ApiResponse<unknown, CinemaImageListResponse> | ApiErrorResponse<unknown>> {
    return cineamoApiClient
        .put(
            `/cinemas/${cinemaId}/images`,
            images.map(({ imageRef, imageDescription }) => ({
                imageRef,
                imageDescription: imageDescription || undefined
            }))
        )
        .then((response) => {
            return response.data;
        })
        .catch((error) => error);
}
